import { supabase } from '../lib/supabase';

// Sanitize filenames for storage keys: strip diacritics, replace invalid chars, keep spaces/._-
const safeName = (name) =>
  // replace remaining invalid chars
  // strip diacritics: é→e, ç→c, à→a
  (name || '')?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.replace(/[^a-zA-Z0-9 ._-]/g, '_')?.trim();

/**
 * Chaine vide -> null, pour les colonnes que Postgres refuse de lire en ''.
 *
 * Motif repris de subcontractorService.js:154-157. PAS `|| null` : celui-ci
 * transformerait aussi 0 et false en null. Ici, seule la chaine vide compte.
 */
const emptyToNull = (v) => (v === '' ? null : v);

function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    out[camelKey] = obj?.[key];
  }
  return out;
}

function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    out[snakeKey] = obj?.[key];
  }
  return out;
}

function mapInvoice(row, paymentRequestsMap) {
  if (!row) return null;
  const inv = toCamelCase(row);
  inv.subcontractor = row?.subcontractors ? toCamelCase(row?.subcontractors) : null;
  // Map payment request from separate fetch instead of join
  const prId = row?.payment_request_id;
  inv.paymentRequest = prId && paymentRequestsMap?.[prId] ? toCamelCase(paymentRequestsMap?.[prId]) : null;
  inv.lines = (row?.sub_invoice_lines || [])?.map(l => {
    const line = toCamelCase(l);
    line.projectCost = l?.project_costs ? toCamelCase(l?.project_costs) : null;
    line.extraCredit = l?.project_extras ? toCamelCase(l?.project_extras) : null;
    line.subcontract = l?.subcontracts ? toCamelCase(l?.subcontracts) : null;
    return line;
  });
  // Plus recente d'abord : la jointure PostgREST ne garantit aucun ordre, et
  // approvals[0] / latestApproval doivent etre la DERNIERE approbation
  // (comme recordPayment et la retenue, qui trient deja par approved_at desc).
  inv.approvals = (row?.sub_invoice_approvals || [])
    ?.map(toCamelCase)
    ?.sort((a, b) => new Date(b?.approvedAt || 0) - new Date(a?.approvedAt || 0));
  inv.payments = (row?.sub_payments || [])?.map(toCamelCase);
  // normalized filter ID — covers all legacy field names
  inv.subcontractFilterId =
    inv?.subcontractId ||
    inv?.contractId ||
    inv?.subcontractorId ||
    null;
  // computed helpers
  inv.invoiceTotal = inv?.lines?.reduce((s, l) => s + (parseFloat(l?.amount) || 0), 0);
  // Prefer stored total_amount (user-corrected); fall back to legacy × 1.14975 for old rows
  inv.invoiceTotalWithTax = (inv?.totalAmount != null)
    ? Number(inv?.totalAmount)
    : inv?.invoiceTotal * 1.14975;
  // Expose stored tax fields for future display (nullable passthrough)
  inv.tpsAmount = inv?.tps_amount ?? null;
  inv.tvqAmount = inv?.tvq_amount ?? null;
  inv.subtotalAmount = inv?.subtotal_amount ?? null;
  const latestApproval = inv?.approvals?.[0] || null;
  inv.latestApproval = latestApproval;
  inv.netPayable = latestApproval ? parseFloat(latestApproval?.netPayable) || 0 : 0;
  inv.totalPaid = inv?.payments?.reduce((s, p) => s + (parseFloat(p?.amountPaid) || 0), 0);
  // balance (Solde): always based on full invoice with tax minus total paid
  inv.balance = Math.max(inv?.invoiceTotalWithTax - inv?.totalPaid, 0);
  // invoice type derived from header references
  if (inv?.creditId) {
    inv.invoiceType = 'credit';
  } else if (inv?.extraId) {
    inv.invoiceType = 'extra';
  } else if (inv?.contractId) {
    inv.invoiceType = 'subcontract';
  } else {
    inv.invoiceType = 'subcontract';
  }
  return inv;
}

// INVOICE_SELECT without payment_requests join (avoids relation error)
const INVOICE_SELECT = `
  *,
  subcontractors ( id, company_name, trade ),
  sub_invoice_lines (
    *,
    project_costs ( id, cost_code, description, budget ),
    project_extras ( id, title, amount ),
    subcontracts ( id, name, pricing_model )
  ),
  sub_invoice_approvals ( * ),
  sub_payments ( * )
`;

// Fetch payment_requests for a project and return as id-keyed map
async function fetchPaymentRequestsMap(projectId) {
  try {
    const { data, error } = await supabase
      ?.from('payment_requests')
      ?.select('id, number, status')
      ?.eq('project_id', projectId);
    if (error || !data) return {};
    return data?.reduce((acc, pr) => { acc[pr.id] = pr; return acc; }, {});
  } catch {
    return {};
  }
}

// Fetch a single invoice by id + separately fetch its payment_request
async function fetchAndMapSingle(invoiceId, projectId) {
  const { data: full, error: fetchErr } = await supabase
    ?.from('sub_invoices')
    ?.select(INVOICE_SELECT)
    ?.eq('id', invoiceId)
    ?.single();
  if (fetchErr) throw fetchErr;

  // Determine projectId for payment_requests lookup
  const pid = projectId || full?.project_id;
  const prMap = pid ? await fetchPaymentRequestsMap(pid) : {};
  return mapInvoice(full, prMap);
}

export const subInvoiceService = {

  async getByProjectId(projectId) {
    try {
      const [{ data, error }, prMap] = await Promise.all([
        supabase?.from('sub_invoices')?.select(INVOICE_SELECT)?.eq('project_id', projectId)?.order('created_at', { ascending: false }),
        fetchPaymentRequestsMap(projectId)
      ]);

      if (error) {
        console.error('[subInvoiceService.getByProjectId]', error?.message);
        return [];
      }
      return (data || [])?.map(row => mapInvoice(row, prMap));
    } catch (err) {
      console.error('[subInvoiceService.getByProjectId]', err);
      return [];
    }
  },

  async create(invoiceData, lines) {
    try {
      // 1. Insert invoice header
      const headerPayload = {
        project_id: invoiceData?.projectId,
        subcontractor_id: invoiceData?.subcontractorId || null,
        subcontract_id: invoiceData?.subcontractId || null,
        contract_id: invoiceData?.contractId || null,
        extra_id: invoiceData?.extraId || null,
        credit_id: invoiceData?.creditId || null,
        invoice_number: invoiceData?.invoiceNumber || null,
        invoice_date: invoiceData?.invoiceDate || null,
        received_date: invoiceData?.receivedDate || null,
        notes: invoiceData?.notes || null,
        payment_request_id: invoiceData?.paymentRequestId || null,
        status: 'received',
        // Tax summary fields
        subtotal_amount: invoiceData?.subtotalAmount != null ? invoiceData?.subtotalAmount : null,
        tps_amount: invoiceData?.tpsAmount != null ? invoiceData?.tpsAmount : null,
        tvq_amount: invoiceData?.tvqAmount != null ? invoiceData?.tvqAmount : null,
        total_amount: invoiceData?.totalAmount != null ? invoiceData?.totalAmount : null,
        tax_applied: invoiceData?.taxApplied != null ? invoiceData?.taxApplied : true,
        retention_included: invoiceData?.retentionIncluded != null ? invoiceData?.retentionIncluded : false,
        retention_percent: invoiceData?.retentionPercent != null ? invoiceData?.retentionPercent : null,
      };

      const { data: invRow, error: invErr } = await supabase?.from('sub_invoices')?.insert(headerPayload)?.select('id')?.single();

      if (invErr) {
        // ── Filet DB : erreur de contrainte unique (race condition) ──
        if (invErr?.code === '23505' && invErr?.message?.includes('uq_sub_invoices_dedup')) {
          const dedupError = new Error('DUPLICATE_INVOICE');
          dedupError.code = 'DUPLICATE_INVOICE';
          throw dedupError;
        }
        throw invErr;
      }

      // 2. Insert lines
      if (lines && lines?.length > 0) {
        const linePayloads = lines?.map(l => ({
          sub_invoice_id: invRow?.id,
          line_type: l?.lineType,
          project_cost_id: l?.lineType === 'cost_code' ? l?.projectCostId : null,
          extra_credit_id: l?.lineType === 'extra_credit' ? l?.extraCreditId : null,
          description: l?.description || null,
          amount: parseFloat(l?.amount) || 0,
          subcontract_id: l?.subcontractId || null
        }));

        const { error: lineErr } = await supabase?.from('sub_invoice_lines')?.insert(linePayloads);

        if (lineErr) throw lineErr;
      }

      // 3. Fetch full record with separate payment_requests lookup
      return await fetchAndMapSingle(invRow?.id, invoiceData?.projectId);
    } catch (err) {
      console.error('[subInvoiceService.create]', err);
      throw err;
    }
  },

  async update(invoiceId, invoiceData, lines) {
    try {
      const headerPayload = {};
      // Les quatre champs qui partaient bruts. Une facture nee dans la boite de
      // reception arrive ici avec received_date = '' — que le formulaire ne
      // permet meme pas de remplir — et Postgres refusait toute modification.
      // CREATE fait deja la conversion (:153-156) ; UPDATE s'aligne.
      if (invoiceData?.invoiceNumber !== undefined) headerPayload.invoice_number = emptyToNull(invoiceData?.invoiceNumber);
      if (invoiceData?.invoiceDate !== undefined) headerPayload.invoice_date = emptyToNull(invoiceData?.invoiceDate);
      if (invoiceData?.receivedDate !== undefined) headerPayload.received_date = emptyToNull(invoiceData?.receivedDate);
      if (invoiceData?.notes !== undefined) headerPayload.notes = emptyToNull(invoiceData?.notes);
      if (invoiceData?.subcontractorId !== undefined) headerPayload.subcontractor_id = invoiceData?.subcontractorId || null;
      if (invoiceData?.subcontractId !== undefined) headerPayload.subcontract_id = invoiceData?.subcontractId || null;
      if (invoiceData?.contractId !== undefined) headerPayload.contract_id = invoiceData?.contractId || null;
      if (invoiceData?.extraId !== undefined) headerPayload.extra_id = invoiceData?.extraId || null;
      if (invoiceData?.creditId !== undefined) headerPayload.credit_id = invoiceData?.creditId || null;
      if (invoiceData?.paymentRequestId !== undefined) headerPayload.payment_request_id = invoiceData?.paymentRequestId || null;
      // Tax summary fields (always write when present)
      if (invoiceData?.subtotalAmount !== undefined) headerPayload.subtotal_amount = invoiceData?.subtotalAmount;
      if (invoiceData?.tpsAmount !== undefined) headerPayload.tps_amount = invoiceData?.tpsAmount;
      if (invoiceData?.tvqAmount !== undefined) headerPayload.tvq_amount = invoiceData?.tvqAmount;
      if (invoiceData?.totalAmount !== undefined) headerPayload.total_amount = invoiceData?.totalAmount;
      if (invoiceData?.taxApplied !== undefined) headerPayload.tax_applied = invoiceData?.taxApplied;
      if (invoiceData?.retentionIncluded !== undefined) headerPayload.retention_included = invoiceData?.retentionIncluded;
      if (invoiceData?.retentionPercent !== undefined) headerPayload.retention_percent = invoiceData?.retentionPercent;

      if (Object.keys(headerPayload)?.length > 0) {
        const { error: updErr } = await supabase?.from('sub_invoices')?.update(headerPayload)?.eq('id', invoiceId);
        if (updErr) throw updErr;
      }

      // Replace lines if provided
      if (lines) {
        await supabase?.from('sub_invoice_lines')?.delete()?.eq('sub_invoice_id', invoiceId);
        if (lines?.length > 0) {
          const linePayloads = lines?.map(l => ({
            sub_invoice_id: invoiceId,
            line_type: l?.lineType,
            project_cost_id: l?.lineType === 'cost_code' ? l?.projectCostId : null,
            extra_credit_id: l?.lineType === 'extra_credit' ? l?.extraCreditId : null,
            description: l?.description || null,
            amount: parseFloat(l?.amount) || 0,
            subcontract_id: l?.subcontractId || null
          }));
          const { error: lineErr } = await supabase?.from('sub_invoice_lines')?.insert(linePayloads);
          if (lineErr) throw lineErr;
        }
      }

      return await fetchAndMapSingle(invoiceId, invoiceData?.projectId);
    } catch (err) {
      console.error('[subInvoiceService.update]', err);
      throw err;
    }
  },

  async delete(invoiceId) {
    try {
      const { error } = await supabase?.from('sub_invoices')?.delete()?.eq('id', invoiceId);
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[subInvoiceService.delete]', err);
      throw err;
    }
  },

  // -------------------------------------------------------
  // Submit for Approval: received -> pending_approval
  // -------------------------------------------------------
  async submitForApproval(invoiceId) {
    try {
      const { error } = await supabase?.from('sub_invoices')
        ?.update({ status: 'pending_approval', updated_at: new Date()?.toISOString() })
        ?.eq('id', invoiceId);
      if (error) throw error;

      return await fetchAndMapSingle(invoiceId);
    } catch (err) {
      console.error('[subInvoiceService.submitForApproval]', err);
      throw err;
    }
  },

  // -------------------------------------------------------
  // Approve: pending_approval -> approved_to_pay
  // -------------------------------------------------------
  async approve(invoiceId, approvalData, lines) {
    try {
      const retainagePercent = parseFloat(approvalData?.retainagePercent) || 0;
      const approvedAmount = parseFloat(approvalData?.approvedAmount) || 0;
      // Optional explicit overrides (retention applied at intake) — otherwise compute
      const retainageAmount = approvalData?.retainageAmount != null
        ? (parseFloat(approvalData?.retainageAmount) || 0)
        : approvedAmount * (retainagePercent / 100);
      const netPayable = approvalData?.netPayable != null
        ? (parseFloat(approvalData?.netPayable) || 0)
        : approvedAmount - retainageAmount;

      // Build validation metadata (all nullable — Tier 1 soft guardrail)
      const pctComplete = approvalData?.estimatedPercentComplete != null
        ? parseFloat(approvalData?.estimatedPercentComplete)
        : null;
      const validatedAmt = approvalData?.validatedAmount != null
        ? parseFloat(approvalData?.validatedAmount)
        : null;
      const overpaymentRisk = approvalData?.overpaymentRisk ?? null;
      const validationNote = approvalData?.validationNote || null;

      // Capture acknowledger identity only when overpayment was acknowledged
      let validationAcknowledgedBy = null;
      let validationAcknowledgedAt = null;
      if (approvalData?.validationAcknowledged === true && overpaymentRisk === true) {
        const { data: { user } } = await supabase?.auth?.getUser();
        validationAcknowledgedBy = user?.id || null;
        validationAcknowledgedAt = new Date()?.toISOString();
      }

      // Insert approval record
      const { error: approvalErr } = await supabase?.from('sub_invoice_approvals')?.insert({
          sub_invoice_id: invoiceId,
          retainage_percent: retainagePercent,
          approved_amount: approvedAmount,
          retainage_amount: retainageAmount,
          net_payable: netPayable,
          payment_note: approvalData?.paymentNote || null,
          client_payment_request_number: approvalData?.clientPaymentRequestNumber || null,
          approved_at: new Date()?.toISOString(),
          // Production validation fields (all nullable)
          estimated_percent_complete: pctComplete,
          validated_amount: validatedAmt,
          overpayment_risk: overpaymentRisk,
          validation_note: validationNote,
          validation_acknowledged_by: validationAcknowledgedBy,
          validation_acknowledged_at: validationAcknowledgedAt,
        });

      if (approvalErr) throw approvalErr;

      // Update invoice status to 'approved_to_pay'
      await supabase?.from('sub_invoices')?.update({ status: 'approved_to_pay', updated_at: new Date()?.toISOString() })?.eq('id', invoiceId);

      const invoice = await fetchAndMapSingle(invoiceId);
      return { success: true, invoice };
    } catch (err) {
      console.error('[subInvoiceService.approve]', err);
      throw err;
    }
  },

  // -------------------------------------------------------
  // Record Payment: stays approved_to_pay until fully paid, then paid
  // -------------------------------------------------------
  async recordPayment(invoiceId, paymentData, netPayable) {
    try {
      // Re-fetch approval from DB to prevent UI manipulation. We need net_payable,
      // approved_amount (brut) and retention_released_at to know the payment ceiling:
      // before release the cap is net_payable; once the retenue is released it becomes
      // approved_amount (the retenue itself becomes payable).
      const { data: approvalRows } = await supabase
        ?.from('sub_invoice_approvals')
        ?.select('net_payable, approved_amount, retainage_amount, retention_released_at')
        ?.eq('sub_invoice_id', invoiceId)
        ?.order('approved_at', { ascending: false })
        ?.limit(1);
      const latestApproval = approvalRows?.[0] || null;
      const dbNetPayable = latestApproval ? parseFloat(latestApproval?.net_payable) || 0 : (netPayable || 0);
      const dbApprovedAmount = latestApproval ? parseFloat(latestApproval?.approved_amount) || 0 : 0;
      const dbRetainageAmount = latestApproval ? parseFloat(latestApproval?.retainage_amount) || 0 : 0;
      const retentionReleased = !!latestApproval?.retention_released_at;
      // Payment ceiling: approved_amount once retention released, else net_payable.
      const paymentCeiling = retentionReleased ? dbApprovedAmount : dbNetPayable;

      // Fetch existing total paid before this payment
      const { data: existingPayments } = await supabase
        ?.from('sub_payments')
        ?.select('amount_paid')
        ?.eq('sub_invoice_id', invoiceId);
      const existingTotalPaid = (existingPayments || [])?.reduce((s, p) => s + (parseFloat(p?.amount_paid) || 0), 0);

      // Guard: payment cannot exceed remaining payable amount (ceiling − already paid)
      const remainingApproved = Math.max(0, paymentCeiling - existingTotalPaid);
      const amountPaid = parseFloat(paymentData?.amountPaid) || 0;
      if (paymentCeiling > 0 && amountPaid > remainingApproved + 0.01) {
        throw new Error(`Payment cannot exceed approved amount (${remainingApproved.toFixed(2)})`);
      }

      const { error: payErr } = await supabase?.from('sub_payments')?.insert({
          sub_invoice_id: invoiceId,
          amount_paid: amountPaid,
          paid_date: paymentData?.paidDate || null,
          method: paymentData?.method || null,
          reference: paymentData?.reference || null,
          check_number: paymentData?.checkNumber || null
        });

      if (payErr) throw payErr;

      // Recalculate total paid from DB (includes new payment)
      const { data: payments } = await supabase?.from('sub_payments')?.select('amount_paid')?.eq('sub_invoice_id', invoiceId);
      const totalPaid = (payments || [])?.reduce((s, p) => s + (parseFloat(p?.amount_paid) || 0), 0);

      // Fetch invoice lines (legacy fallback) + current status for status logic
      const { data: invRow } = await supabase
        ?.from('sub_invoices')
        ?.select('status, sub_invoice_lines ( amount )')
        ?.eq('id', invoiceId)
        ?.single();

      // Status logic:
      // - With an approval: 'paid' when totalPaid >= approved_amount (brut) AND
      //   (no retenue OR retenue released) ; else 'partially_paid'. A facture with an
      //   un-released retenue therefore stays 'partially_paid' — jamais 'paid' par erreur.
      // - Without an approval: legacy fallback on the full invoice total incl. tax.
      let newStatus;
      if (totalPaid <= 0) {
        newStatus = invRow?.status || 'approved_to_pay';
      } else if (latestApproval) {
        const fullyPaid = totalPaid >= dbApprovedAmount - 0.01 && (dbRetainageAmount === 0 || retentionReleased);
        newStatus = fullyPaid ? 'paid' : 'partially_paid';
      } else {
        const rawLines = invRow?.sub_invoice_lines || [];
        const invoiceTotal = rawLines?.reduce((s, l) => s + (parseFloat(l?.amount) || 0), 0);
        const invoiceTotalWithTax = invoiceTotal * 1.14975;
        newStatus = totalPaid >= invoiceTotalWithTax - 0.01 ? 'paid' : 'partially_paid';
      }

      await supabase?.from('sub_invoices')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', invoiceId);

      return await fetchAndMapSingle(invoiceId);
    } catch (err) {
      console.error('[subInvoiceService.recordPayment]', err);
      throw err;
    }
  },

  // -------------------------------------------------------
  // Retention release — request (PM clicks « Payer la retenue »)
  // Sets the flag on the LATEST approval; the facture réapparaît en Approbation.
  // -------------------------------------------------------
  async requestRetentionRelease(invoiceId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: rows } = await supabase
        ?.from('sub_invoice_approvals')
        ?.select('id')
        ?.eq('sub_invoice_id', invoiceId)
        ?.order('approved_at', { ascending: false })
        ?.limit(1);
      const approvalId = rows?.[0]?.id;
      if (!approvalId) throw new Error('NO_APPROVAL');
      const { error } = await supabase?.from('sub_invoice_approvals')?.update({
        retention_release_requested: true,
        retention_release_requested_at: new Date()?.toISOString(),
        retention_release_requested_by: user?.id || null,
      })?.eq('id', approvalId);
      if (error) throw error;
      return await fetchAndMapSingle(invoiceId);
    } catch (err) {
      console.error('[subInvoiceService.requestRetentionRelease]', err);
      throw err;
    }
  },

  // -------------------------------------------------------
  // Retention release — confirm (quittance finale signée vérifiée en amont)
  // Sets retention_released_at/by on the LATEST approval; la retenue devient payable.
  // -------------------------------------------------------
  async releaseRetention(invoiceId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: rows } = await supabase
        ?.from('sub_invoice_approvals')
        ?.select('id')
        ?.eq('sub_invoice_id', invoiceId)
        ?.order('approved_at', { ascending: false })
        ?.limit(1);
      const approvalId = rows?.[0]?.id;
      if (!approvalId) throw new Error('NO_APPROVAL');
      const { error } = await supabase?.from('sub_invoice_approvals')?.update({
        retention_released_at: new Date()?.toISOString(),
        retention_released_by: user?.id || null,
      })?.eq('id', approvalId);
      if (error) throw error;
      return await fetchAndMapSingle(invoiceId);
    } catch (err) {
      console.error('[subInvoiceService.releaseRetention]', err);
      throw err;
    }
  },

  // -------------------------------------------------------
  // Remise a recue — ce que la remise radierait, lu en DB (pas l'ecran)
  // -------------------------------------------------------
  async getResetImpact(invoiceId) {
    try {
      const [payRes, apprRes] = await Promise.all([
        supabase
          ?.from('sub_payments')
          ?.select('id, check_number, amount_paid, paid_date, method')
          ?.eq('sub_invoice_id', invoiceId)
          ?.order('paid_date', { ascending: true }),
        supabase
          ?.from('sub_invoice_approvals')
          ?.select('id, net_payable, approved_at')
          ?.eq('sub_invoice_id', invoiceId)
          ?.order('approved_at', { ascending: false }),
      ]);
      if (payRes?.error) throw payRes.error;
      if (apprRes?.error) throw apprRes.error;
      return { invoiceId, payments: payRes?.data || [], approvals: apprRes?.data || [] };
    } catch (err) {
      console.error('[subInvoiceService.getResetImpact]', err);
      throw err;
    }
  },

  // -------------------------------------------------------
  // Remise a recue — RPC en une transaction : supprime sub_payments +
  // sub_invoice_approvals de la facture, statut -> received.
  // Retourne { payments_deleted, approvals_deleted }.
  // -------------------------------------------------------
  async resetToReceived(invoiceId) {
    try {
      const { data, error } = await supabase?.rpc('reset_sub_invoice_to_received', { p_invoice_id: invoiceId });
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('[subInvoiceService.resetToReceived]', err);
      throw err;
    }
  },

  // Get default retainage % from subcontract linked to a project_cost_id
  async getDefaultRetainage(projectId, projectCostId) {
    try {
      const { data } = await supabase?.from('subcontracts')?.select('notes')?.eq('project_id', projectId)?.eq('project_cost_id', projectCostId)?.limit(1);
      return 0;
    } catch {
      return 0;
    }
  },

};

export default subInvoiceService;
